/**
 * Statuts des devis tels que le CLIENT les voit, dérivés — jamais stockés, comme
 * les statuts back-office (`calculateEstimateStatus`). Pur et sans dépendance :
 * l'espace client, la page publique et les tests partagent cette seule règle.
 */
/**
 * Libellés client des statuts de demande. Une seule source : l'espace client, la
 * page publique du devis et les tests affichent le MÊME mot.
 *
 * `TARIFFS_LATER` se dit « Tarifs indisponibles » côté client, là où le
 * back-office parle de « Pour plus tard » : le client n'a pas à connaître notre
 * geste de rangement, il a besoin de savoir que les tarifs ne sont pas publiés
 * et à partir de quand ils le seront (DEVIS-CLIENT-PPT).
 */
export const MY_ESTIMATE_REQUEST_STATUS_LABELS = {
    ACTIVE: 'En cours',
    CLOSED: 'Terminée',
    PROCESSING: 'En cours de préparation',
    RESERVATION_REQUESTED: 'Réservation en attente',
    TARIFFS_LATER: 'Tarifs indisponibles',
};
const toTime = (value) => {
    if (!value)
        return null;
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};
/**
 * Les trois actions (confirmer / refuser / demander une modification) ne sont
 * ouvertes que sur ces deux statuts. Calculé côté serveur et renvoyé au front :
 * la règle ne se réécrit pas dans l'interface, ni dans l'espace client, ni sur
 * la page publique.
 */
export function canActOnMyEstimateResponse(status) {
    return status === 'AWAITING_CLIENT' || status === 'MODIFICATION_REQUESTED';
}
/**
 * Statut de la DEMANDE, agrégé depuis ses propositions. `TARIFFS_LATER` prime :
 * une demande rangée « Pour plus tard » n'a pas de proposition et le client doit
 * lire le message d'horizon tarifaire, pas « nos équipes vous répondent ».
 *
 * Vient ensuite `RESERVATION_REQUESTED`, AVANT « En cours ». Une réservation
 * demandée est le seul geste du client qui n'attend plus rien de lui : la
 * confondre avec « En cours » lui laissait croire que sa demande n'était pas
 * partie. Le cas d'une proposition encore actionnable à côté d'une réservation
 * en cours n'existe pas en pratique — confirmer décline automatiquement les
 * sœurs — mais la règle doit trancher, et elle tranche pour l'état le plus
 * avancé.
 */
export function deriveMyEstimateRequestStatus(params) {
    if (params.snoozed)
        return 'TARIFFS_LATER';
    if (params.responseStatuses.length === 0)
        return 'PROCESSING';
    if (params.responseStatuses.includes('RESERVATION_REQUESTED'))
        return 'RESERVATION_REQUESTED';
    if (params.responseStatuses.some(canActOnMyEstimateResponse))
        return 'ACTIVE';
    return 'CLOSED';
}
/**
 * Priorité : réservation > refus > expiration > modification > attente.
 *
 * L'expiration passe AVANT la demande de modification : une proposition périmée
 * n'est plus actionnable, quoi qu'on ait demandé dessus. L'agence répondra par
 * une nouvelle proposition, qui, elle, sera actionnable.
 */
export function deriveMyEstimateResponseStatus(input, now = new Date()) {
    if (input.hasBooking || input.reservation?.isTreated)
        return 'CONFIRMED';
    if (input.reservation)
        return 'RESERVATION_REQUESTED';
    if (input.declinedAt) {
        return input.declineReason === 'OTHER_OFFER_ACCEPTED' ? 'DECLINED_AUTO' : 'DECLINED';
    }
    const validTo = toTime(input.validTo);
    if (validTo !== null && validTo <= now.getTime())
        return 'EXPIRED';
    const modificationAt = toTime(input.lastModificationRequestAt);
    if (modificationAt !== null) {
        const dispatchAt = toTime(input.lastRequestDispatchAt);
        if (dispatchAt === null || modificationAt > dispatchAt)
            return 'MODIFICATION_REQUESTED';
    }
    return 'AWAITING_CLIENT';
}
